import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * TryOnCreditsContext — single source of truth for the user's AI try-on
 * credit balance. Header pill, PDP button, and paywall all consume this so
 * the number updates instantly after a purchase.
 */
const TryOnCreditsContext = createContext(null);

export function TryOnCreditsProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ credits: 0, is_vip: false, remaining: 0, loading: false });

  const refresh = useCallback(async () => {
    if (!user) { setState({ credits: 0, is_vip: false, remaining: 0, loading: false }); return; }
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.tryOnCredits();
      setState({ credits: data.credits || 0, is_vip: !!data.is_vip, remaining: data.remaining, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <TryOnCreditsContext.Provider value={{ ...state, refresh }}>
      {children}
    </TryOnCreditsContext.Provider>
  );
}

export const useTryOnCredits = () => {
  const ctx = useContext(TryOnCreditsContext);
  if (!ctx) throw new Error("useTryOnCredits must be used within TryOnCreditsProvider");
  return ctx;
};
